// src/hooks/useCostMode.js
//
// PR-0a (Maliyet Kalkanı) — Minimal costMode okuyucu.
// system/costMode dokümanındaki { mode: 'normal' | 'limited' | 'emergency' }
// alanını canlı dinler. Doküman yoksa, okunamazsa (rules izni henüz
// açılmadıysa) veya alan geçersizse GÜVENLİ varsayılan: 'normal'.
//
// Tüm bileşenler TEK paylaşılan listener üzerinden beslenir (modül seviyesi
// singleton) — her bileşen kendi onSnapshot'ını açmaz.
//
// Not: Tam costMode sistemi (admin paneli, backend kontrolleri, kullanıcı
// bilgi çubuğu) PR-1 kapsamı. Bu dosya yalnızca okuma katmanıdır.

import { useEffect, useState } from 'react';
import { db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';

export const COST_MODES = ['normal', 'limited', 'emergency'];

let currentMode = 'normal';
let unsubscribe: (() => void) | null = null;
let lastStartAttemptMs = 0;
const subscribers = new Set<(mode: string) => void>();

const RETRY_BACKOFF_MS = 60_000;

function notify() {
  subscribers.forEach((fn) => {
    try { fn(currentMode); } catch {}
  });
}

function startListener() {
  if (unsubscribe) return;
  const now = Date.now();
  // Hata sonrası agresif yeniden deneme yok — 60 sn backoff
  if (now - lastStartAttemptMs < RETRY_BACKOFF_MS) return;
  lastStartAttemptMs = now;

  try {
    unsubscribe = onSnapshot(
      doc(db, 'system', 'costMode'),
      (snap) => {
        const m = snap.exists() ? snap.data()?.mode : 'normal';
        currentMode = COST_MODES.includes(m) ? m : 'normal';
        notify();
      },
      () => {
        // permission-denied vb. — listener öldü; güvenli varsayılana dön,
        // bir sonraki hook mount'unda (backoff sonrası) yeniden dene.
        unsubscribe = null;
        currentMode = 'normal';
        notify();
      }
    );
  } catch {
    unsubscribe = null;
    currentMode = 'normal';
  }
}

/** Hook dışı erişim için anlık değer (servis katmanı kullanımı). */
export function getCostMode() {
  return currentMode;
}

/* PR-1 — Mod başına davranış ayarları (tek kaynak).
   Ekranlar bu değerleri kullanır; yeni bir kısma eklemek istediğinde
   buraya alan ekle, ilgili ekranda oku. */
const MODE_CONFIG = {
  normal: {
    feedPostsLimit: 20,      // ana feed gönderi sorgusu limiti
    feedClipsPageSize: 10,   // ana feed klip sayfa boyutu
    explorePageSize: 24,     // keşfet grid sayfa boyutu
    searchDebounceMs: 300,   // arama yazarken bekleme süresi
    videoPreload: 'metadata',// feed videolarının ön yükleme modu
    allowVideoUpload: true,  // yeni video yükleme açık mı
    showBanner: false,       // kullanıcı bilgi çubuğu
  },
  limited: {
    feedPostsLimit: 10,
    feedClipsPageSize: 5,
    explorePageSize: 12,
    searchDebounceMs: 800,
    videoPreload: 'none',    // otomatik video ön yükleme kapalı
    allowVideoUpload: true,
    showBanner: false,
  },
  emergency: {
    feedPostsLimit: 10,
    feedClipsPageSize: 5,
    explorePageSize: 12,
    searchDebounceMs: 1500,
    videoPreload: 'none',
    allowVideoUpload: false, // yeni video yükleme kapalı
    showBanner: true,
  },
};

/** Mod adı — `MODE_CONFIG` anahtarlarından TÜRETİLDİ (elle sayılmadı). */
export type MaliyetModu = keyof typeof MODE_CONFIG;
/** Mod ayarları — yine `MODE_CONFIG`ten türetildi. */
export type MaliyetModuAyari = (typeof MODE_CONFIG)[MaliyetModu];

// `mode` `string`: çağıranlar (servis katmanı) serbest metin veriyor ve
// gövdedeki `|| MODE_CONFIG.normal` yedeği tam bunun için var.
export function getCostModeConfig(mode: string): MaliyetModuAyari {
  return MODE_CONFIG[mode as MaliyetModu] || MODE_CONFIG.normal;
}

/** Kullanıcıya gösterilen kısıtlama mesajı (emergency). */
export const COST_MODE_BANNER_TEXT =
  'Yoğunluk nedeniyle bazı özellikler geçici olarak kısıtlandı.';

export default function useCostMode() {
  const [mode, setMode] = useState(currentMode);

  useEffect(() => {
    startListener();
    const fn = (m: typeof currentMode) => setMode(m);
    subscribers.add(fn);
    setMode(currentMode);
    return () => { subscribers.delete(fn); };
  }, []);

  return mode;
}
